/**
 * Created by yangxy on 2022/11/24.
 * 播放器初始化参数处理
 */
import { ValidationError } from '../common/error-code';
import { deepAssign, guid, isPlainObject, probe } from '../common/Utils';
import { THEMEOPTIONS } from '../common/Constant';
import defaultOption from './default';
import log from 'loglevel';
/**
 * 通用的options参数处理
 * @param {object} options
 * @returns
 */
export default function (options) {
  // options必须是一个对象
  if (!isPlainObject(options)) return defaultOption;

  // 合并数据
  options = deepAssign({}, defaultOption, options);

  // 必填参数验证
  if (
    !options.appId ||
    !options.accountId ||
    !options.token ||
    !options.type ||
    typeof options.type !== 'string' ||
    !options.videoNode
  ) {
    throw new ValidationError();
  }

  // 播放类型参数验证
  options.type = options.type.toLowerCase();
  if (!['live', 'vod'].includes(options.type)) {
    throw new ValidationError('参数 type 错误, 可选值：live、vod');
  }

  // 增加是否是直播的属性
  options.isLive = options.type === 'live';

  // 直播场景参数验证
  if (options.type === 'live') {
    if (!isPlainObject(options.liveOption) || !options.liveOption.roomId) {
      throw new ValidationError('参数 liveOption 错误');
    }
    // 小写转换
    options.liveOption.type = (options.liveOption.type || '').toLowerCase();
    if (!['auto', 'hls', 'flv', 'rtmp', 'native', 'rtl'].includes(options.liveOption.type)) {
      throw new ValidationError('参数 liveOption 错误');
    }
    // rtl直播自动降级配置，如果不合法，设置默认值
    if (typeof options.liveOption.autodegrade !== 'boolean') {
      options.liveOption.autodegrade = true;
    }
    // 默认播放清晰度
    if (!options.liveOption.defaultDefinition) {
      options.liveOption.defaultDefinition = '480p'; // 默认 480P
    }
  }

  // 点播场景参数验证
  if (options.type === 'vod') {
    if (!isPlainObject(options.vodOption) || !options.vodOption.recordId) {
      throw new ValidationError('参数 vodOption 错误');
    }
    if (!options.vodOption.defaultDefinition) {
      options.vodOption.defaultDefinition = '480p'; // 默认 480P
    }
  }

  // 主题配置校验, 如果不是可选值，设置默认值
  if (!THEMEOPTIONS.includes(options.theme)) {
    options.theme = 'classic';
  }

  // 多语言, 如果不是可选值，设置默认值
  if (!['en', 'zh'].includes(options.language)) {
    options.language = 'en';
  }

  if (!isPlainObject(options.reportOption) || !options.reportOption.interval) {
    throw new ValidationError('参数 reportOption 错误');
  }
  options.reportOption.interval = parseInt(options.reportOption.interval);
  if (options.reportOption.interval < 3 || options.reportOption.interval > 90) {
    options.reportOption.interval = 30;
  }

  // 扩展一些公用属性
  if (options.type === 'live') {
    options.defaultDefinition = options.liveOption.defaultDefinition;
    options.webinar_id = options.liveOption.roomId.trim();
  } else {
    options.defaultDefinition = options.vodOption.defaultDefinition;
    options.webinar_id = options.vodOption.recordId.trim();
  }

  // 创建唯一id
  options._uuid = guid();

  // rtl直播降级判断处理
  if (options.type === 'live' && options.liveOption.type === 'rtl') {
    probe(
      {},
      info => {
        if (!info.WebRTC && options.liveOption.autodegrade) {
          // 不支持webrtc，但是开启了自动降级，则报直播方式改为常规直播
          log.info('不支持WebRTC，自动降级为普通直播协议！');
          options.liveOption.type = 'auto'; // rtl --> auto
        }
      },
      e => {
        // TODO 异常处理
      }
    );
  }

  // 弹幕，跑马灯字体单位转小写
  options.barrageSetting.style.unit = (options.barrageSetting.style.unit || '').toLowerCase();
  options.barrageSetting.style.h5Unit = (options.barrageSetting.style.h5Unit || '').toLowerCase();
  options.marqueeOption.unit = (options.marqueeOption.unit || '').toLowerCase();
  options.marqueeOption.h5Unit = (options.marqueeOption.h5Unit || '').toLowerCase();

  // 校验 弹幕 字体单位
  const FONTSIZE_UNITS = ['px', 'rem', 'em', '%', 'vw', 'vh'];
  if (
    !FONTSIZE_UNITS.includes(options.barrageSetting.style.unit) ||
    !FONTSIZE_UNITS.includes(options.barrageSetting.style.h5Unit)
  ) {
    throw new ValidationError('参数 barrageSetting unit 或者 h5Unit 错误');
  }

  // 校验 跑马灯 字体单位
  if (!FONTSIZE_UNITS.includes(options.marqueeOption.unit) || !FONTSIZE_UNITS.includes(options.marqueeOption.h5Unit)) {
    throw new ValidationError('参数 marqueeOption unit 或者 h5Unit 错误');
  }

  return options;
}

export const getSomeConfig = function (name, opt, serverData) {
  switch (name) {
    case 'reportConfig': {
      return {
        url: serverData?.log_server,
        webinar_id: opt.webinar_id,
        uid: serverData?.log_info?.uid,
        session_id: serverData?.log_info?.session_id,
        vid: serverData?.log_info?.account_id,
        vfid: serverData?.log_info?.account_id,
        // 移动端上报  paas为空
        ndi: '',
        // saas平台上报  paas为空
        guid: '',
        // saas平台上报  paas为空
        vtype: '',
        // saas平台上报  paas为空
        topic: '',
        app_id: opt.appId,
        flow_type: 2, // 流量类型  默认为2
        bu: 1, // 业务类型 pass 为1
        enable: opt?.reportOption?.enable, // 是否启用日志上报功能
        interval: opt?.reportOption?.interval || 30
      };
    }
    case 'schedulerConfig': {
      return {
        app_id: opt.appId,
        webinar_id: opt.webinar_id,
        uid: serverData?.log_info?.uid,
        bu: 1,
        gslb_server: serverData?.dispatch_server, //播放调度服务域名，如https://gslb.e.vhall.com
        defaultDef: opt?.defaultDefinition, // 默认清晰度
        live_type: opt.type === 'live' ? +serverData.live_type : null, // 0-普通直播 1-时移直播
        live_subtitle: opt.type === 'live' ? +serverData.live_subtitle : null, // 0/1，是否开启自研实时字幕
        live_subtitle_mps: opt.type === 'live' ? serverData.live_subtitle_mps : null, // string，是否开启腾讯云MPS实时字幕
        live_subtitle_enable: opt.liveSubtitleOption['enable'], //播放器是否启用字幕能力
        live_subtitle_auto: opt.liveSubtitleOption['auto'], //播放器是否自动开启播放字幕
        live_delay: opt.type === 'live' ? +serverData.live_delay : null, // 0-非延时直播 1-延时直播
        force_ignore_delay: opt.forceIgnoreDelay, // 延播场景下，是否强制忽略延迟，观看普通延迟直播流
        app_custom_line: opt.appCustomLine,
        cipher_key: serverData.needDecrypt && serverData.safe_server ? serverData.safe_server.cipher_key : null,
        // 直播清晰度列表默认走调度，点播默认从接口获取；
        // 如果有传入强制清晰度列表，则清晰度列表以传入列表为准；
        quality: Array.isArray(opt?.schedulerOption?.quality)
          ? opt.schedulerOption.quality
          : opt.type === 'live'
          ? []
          : serverData.needDecrypt
          ? serverData.safe_definition
          : serverData.definition // 要获取的清晰度列表   直播传空数组
      };
    }
    case 'liveTimeshiftConfig': {
      return {
        enable: false,
        isLive: opt.type === 'live',
        // 是否开启时移
        isOpenTimeshift: opt.type === 'live' ? serverData.live_type === 1 : undefined,
        // 当前已推流时长
        live_duration: opt.type === 'live' && serverData.live_type === 1 ? serverData.live_duration : undefined
      };
    }
    case 'watermarkConfig': {
      let align = '';
      switch (serverData?.watermark?.position) {
        case 1: // 左上角
          align = 'tl';
          break;
        case 2: // 右上角
          align = 'tr';
          break;
        case 3: // 右下角
          align = 'br';
          break;
        case 4: // 左下角
          align = 'bl';
          break;
        default:
          break;
      }
      if (serverData?.watermark?.url) {
        // 如果服务端配置了水印url就用服务端的，没有就用前端配置的
        return Object.assign({}, opt.watermarkOption, {
          enable: true,
          align,
          url: serverData?.watermark?.url
        });
      } else {
        return Object.assign({}, opt.watermarkOption);
      }
    }
    case 'vodSubtitleConfig': {
      // 点播字幕插件配置
      return Object.assign(
        {
          subtitle_info: serverData?.subtitle_info
        },
        opt.vodSubtitleOption
      );
    }
    case 'liveSubtitleConfig': {
      // 直播字幕插件配置
      return {
        live_subtitle: opt.type === 'live' ? +serverData.live_subtitle : null, // 0/1，是否开启自研实时字幕
        live_subtitle_mps: opt.type === 'live' ? serverData.live_subtitle_mps : null, // string，是否开启腾讯云MPS实时字幕
        live_subtitle_enable: opt.liveSubtitleOption['enable'], //播放器是否启用字幕能力
        live_subtitle_auto: opt.liveSubtitleOption['auto'] //播放器是否自动开启播放字幕
      };
    }
    case 'barrageConfig': {
      // 从本地缓存取弹幕配置
      let barrageSettingFromStorage = null;
      // 合并弹幕默认配置和传参弹幕配置
      if (opt.barrageSetting.useLocalCache) {
        try {
          barrageSettingFromStorage = JSON.parse(localStorage.getItem(opt.barrageSetting.localCacheKey));
        } catch (err) {
          log.warn('Error occurred when getting barrageSetting from cache');
        }
      }
      return deepAssign({}, opt.barrageSetting, barrageSettingFromStorage);
    }
  }
};
